import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";

import {
  useGenerateAccountHistoryFromOrionMutation,
  useGenerateOptionCoverageMutation,
} from "features/trading/accounts";
import { NOTISTACK_FAILURE_OPTIONS } from "constants/index";
import { BalanceTypeEnum, OptionTypeEnum } from "helpers/types";

const GENERATION_ERROR_MESSAGE: Record<BalanceTypeEnum, string> = {
  [BalanceTypeEnum.TRADE_AMOUNT]:
    "Couldn't create trade amount history for this account.",
  [BalanceTypeEnum.PORTFOLIO]:
    "Couldn't generate portfolio history for this account.",
};

export interface AccountHistoryGeneration {
  generate: (balanceType: BalanceTypeEnum) => Promise<void>;
  isGeneratingTradeAmount: boolean;
  isGeneratingPortfolio: boolean;
}

export const useAccountHistoryGeneration = (
  accountId: number | null | undefined,
  assetId: number | null | undefined,
  optionType: OptionTypeEnum | undefined,
): AccountHistoryGeneration => {
  const [generateOptionCoverage, { isLoading: isGeneratingTradeAmount }] =
    useGenerateOptionCoverageMutation();
  const [generateFromOrion, { isLoading: isGeneratingPortfolio }] =
    useGenerateAccountHistoryFromOrionMutation();

  const generate = useCallback(
    async (balanceType: BalanceTypeEnum) => {
      if (!accountId || !assetId) return;
      try {
        if (balanceType === BalanceTypeEnum.TRADE_AMOUNT) {
          if (!optionType) return;
          await generateOptionCoverage({
            account_id: accountId,
            asset_id: assetId,
            option_type: optionType,
          }).unwrap();
        } else {
          await generateFromOrion({
            account_id: accountId,
            asset_id: assetId,
          }).unwrap();
        }
      } catch {
        enqueueSnackbar(
          GENERATION_ERROR_MESSAGE[balanceType],
          NOTISTACK_FAILURE_OPTIONS,
        );
      }
    },
    [accountId, assetId, optionType, generateOptionCoverage, generateFromOrion],
  );

  return {
    generate,
    isGeneratingTradeAmount,
    isGeneratingPortfolio,
  };
};
